import { Scene } from '@babylonjs/core/scene';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Animation } from '@babylonjs/core/Animations/animation';
import { HighlightLayer } from '@babylonjs/core/Layers/highlightLayer';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';

import '@babylonjs/core/Animations/animatable';
import '@babylonjs/core/Meshes/Builders/sphereBuilder';
import '@babylonjs/core/Meshes/Builders/planeBuilder';
import '@babylonjs/core/Meshes/Builders/tubeBuilder';
import '@babylonjs/core/Meshes/Builders/cylinderBuilder';

/**
 * 链表节点元素
 */
interface LinkedListNodeElement {
  mesh: Mesh;
  label: Mesh;
  arrow: Mesh | null;
  value: number;
  position: Vector3;
}

/**
 * 链表可视化器
 */
export class LinkedListVisualizer {
  private scene: Scene;
  private highlightLayer: HighlightLayer;
  private nodes: LinkedListNodeElement[] = [];
  
  // 可视化参数
  private readonly nodeRadius = 0.6;
  private readonly spacing = 2.5;
  private readonly arrowLength = 1.2;
  
  // 颜色定义 - 柔和马卡龙色系
  private readonly colors = {
    default: new Color3(0.55, 0.75, 0.95),   // 天空蓝
    head: new Color3(1.0, 0.7, 0.5),         // 桃色
    tail: new Color3(0.55, 0.88, 0.7),       // 薄荷绿
    current: new Color3(1.0, 0.85, 0.4),     // 暖黄色
    highlight: new Color3(1.0, 0.6, 0.65),   // 珊瑚粉
    arrow: new Color3(0.7, 0.7, 0.8),        // 淡紫灰
  };

  constructor(scene: Scene, highlightLayer: HighlightLayer) {
    this.scene = scene;
    this.highlightLayer = highlightLayer;
  }

  /**
   * 从数组创建链表可视化
   */
  async createFromArray(array: number[]): Promise<void> {
    this.clear();
    
    const offset = (array.length - 1) * this.spacing / 2;
    
    for (let i = 0; i < array.length; i++) {
      const x = i * this.spacing - offset;
      const position = new Vector3(x, 1, 0);
      
      const node = this.createNodeSync(array[i], position, i);
      this.nodes.push(node);
      
      // 创建指向下一个节点的箭头
      if (i < array.length - 1) {
        const nextX = (i + 1) * this.spacing - offset;
        node.arrow = this.createArrow(position, new Vector3(nextX, 1, 0));
      }
    }
    
    // 标记头尾节点
    if (this.nodes.length > 0) {
      this.setNodeColor(0, this.colors.head);
      if (this.nodes.length > 1) {
        this.setNodeColor(this.nodes.length - 1, this.colors.tail);
      }
    }
    
    console.log(`✅ 已创建链表 ${this.nodes.length} 个节点`);
  }

  /**
   * 同步创建节点（无动画）
   */
  private createNodeSync(value: number, position: Vector3, index: number): LinkedListNodeElement {
    // 创建球体节点
    const mesh = MeshBuilder.CreateSphere(
      `linkedNode_${index}`,
      { diameter: this.nodeRadius * 2 },
      this.scene
    );
    mesh.position = position.clone();
    
    // 材质
    const material = new StandardMaterial(`nodeMat_${index}`, this.scene);
    material.diffuseColor = this.colors.default;
    material.specularColor = new Color3(0.4, 0.4, 0.4);
    mesh.material = material;
    
    // 标签
    const label = this.createLabel(value.toString(), mesh);
    
    return {
      mesh,
      label,
      arrow: null,
      value,
      position: position.clone()
    };
  }

  /**
   * 创建单个节点
   */
  private async createNode(value: number, position: Vector3, index: number): Promise<LinkedListNodeElement> {
    // 创建球体节点
    const mesh = MeshBuilder.CreateSphere(
      `linkedNode_${index}`,
      { diameter: this.nodeRadius * 2 },
      this.scene
    );
    mesh.position = position.clone();
    mesh.position.y = 5; // 初始位置在上方
    
    // 材质
    const material = new StandardMaterial(`nodeMat_${index}`, this.scene);
    material.diffuseColor = this.colors.default;
    material.specularColor = new Color3(0.4, 0.4, 0.4);
    mesh.material = material;
    
    // 标签
    const label = this.createLabel(value.toString(), mesh);
    
    // 入场动画
    await this.animateNodeIn(mesh, position);
    
    return {
      mesh,
      label,
      arrow: null,
      value,
      position: position.clone()
    };
  }

  /**
   * 创建箭头
   */
  private createArrow(from: Vector3, to: Vector3): Mesh {
    const direction = to.subtract(from).normalize();
    const start = from.add(direction.scale(this.nodeRadius + 0.1));
    const end = to.subtract(direction.scale(this.nodeRadius + 0.1));
    
    // 创建箭头主体（线）
    const tube = MeshBuilder.CreateTube(
      'arrow',
      {
        path: [start, end.subtract(direction.scale(0.3))],
        radius: 0.05,
        tessellation: 8
      },
      this.scene
    );
    
    // 创建箭头头部
    const cone = MeshBuilder.CreateCylinder(
      'arrowHead',
      { height: 0.3, diameterTop: 0, diameterBottom: 0.2, tessellation: 8 },
      this.scene
    );
    cone.position = end.subtract(direction.scale(0.15));
    cone.rotation.z = -Math.PI / 2;
    cone.rotation.y = Math.atan2(direction.z, direction.x);
    
    // 合并网格
    const arrow = Mesh.MergeMeshes([tube, cone], true);
    
    if (arrow) {
      const material = new StandardMaterial('arrowMat', this.scene);
      material.diffuseColor = this.colors.arrow;
      material.emissiveColor = new Color3(0.1, 0.1, 0.1);
      arrow.material = material;
      return arrow;
    }
    
    return tube;
  }

  /**
   * 创建标签
   */
  private createLabel(text: string, parent: Mesh): Mesh {
    const plane = MeshBuilder.CreatePlane(
      `label_${text}`,
      { width: 0.8, height: 0.5 },
      this.scene
    );
    
    const texture = new DynamicTexture(
      `labelTex_${text}`,
      { width: 128, height: 64 },
      this.scene,
      false
    );
    texture.hasAlpha = true;
    texture.drawText(text, null, null, 'bold 36px Arial', 'white', 'transparent', true);
    
    const material = new StandardMaterial(`labelMat_${text}`, this.scene);
    material.diffuseTexture = texture;
    material.emissiveColor = new Color3(1, 1, 1);
    material.disableLighting = true;
    material.backFaceCulling = false;
    plane.material = material;
    
    plane.parent = parent;
    plane.position.y = this.nodeRadius + 0.5;
    plane.billboardMode = Mesh.BILLBOARDMODE_ALL;
    
    return plane;
  }

  /**
   * 节点入场动画
   */
  private animateNodeIn(mesh: Mesh, targetPosition: Vector3): Promise<void> {
    return new Promise(resolve => {
      const animation = new Animation(
        'nodeIn',
        'position.y',
        60,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CONSTANT
      );
      
      animation.setKeys([
        { frame: 0, value: 5 },
        { frame: 20, value: targetPosition.y }
      ]);
      
      mesh.animations = [animation];
      this.scene.beginAnimation(mesh, 0, 20, false, 1, resolve);
    });
  }

  /**
   * 高亮节点
   */
  async highlight(index: number, duration = 500): Promise<void> {
    if (this.nodes[index]) {
      this.setNodeColor(index, this.colors.current);
      this.highlightLayer.addMesh(this.nodes[index].mesh, Color3.Yellow());
      await this.delay(duration);
    }
  }

  /**
   * 取消高亮
   */
  async unhighlight(index: number): Promise<void> {
    if (this.nodes[index]) {
      // 恢复原色
      if (index === 0) {
        this.setNodeColor(index, this.colors.head);
      } else if (index === this.nodes.length - 1) {
        this.setNodeColor(index, this.colors.tail);
      } else {
        this.setNodeColor(index, this.colors.default);
      }
      this.highlightLayer.removeMesh(this.nodes[index].mesh);
    }
  }

  /**
   * 插入节点动画
   */
  async insert(value: number, atIndex: number, duration = 500): Promise<void> {
    // 计算新节点位置
    const offset = this.nodes.length * this.spacing / 2;
    const x = atIndex * this.spacing - offset;
    const position = new Vector3(x, 1, 0);
    
    // 移动后面的节点
    for (let i = atIndex; i < this.nodes.length; i++) {
      const newX = (i + 1) * this.spacing - offset;
      await this.animatePosition(this.nodes[i].mesh, new Vector3(newX, 1, 0), duration);
      this.nodes[i].position.x = newX;
    }
    
    // 创建新节点
    const node = await this.createNode(value, position, atIndex);
    
    // 更新箭头
    this.updateArrows();
    
    // 插入到数组
    this.nodes.splice(atIndex, 0, node);
  }

  /**
   * 删除节点动画
   */
  async remove(atIndex: number, duration = 500): Promise<void> {
    if (atIndex < 0 || atIndex >= this.nodes.length) return;
    
    const node = this.nodes[atIndex];
    
    // 淡出动画
    await this.animateFadeOut(node.mesh, duration);
    
    // 清理
    node.label.dispose();
    node.mesh.dispose();
    if (node.arrow) node.arrow.dispose();
    
    // 从数组移除
    this.nodes.splice(atIndex, 1);
    
    // 重新定位剩余节点
    const offset = (this.nodes.length - 1) * this.spacing / 2;
    for (let i = 0; i < this.nodes.length; i++) {
      const newX = i * this.spacing - offset;
      await this.animatePosition(this.nodes[i].mesh, new Vector3(newX, 1, 0), duration);
      this.nodes[i].position.x = newX;
    }
    
    // 更新箭头
    this.updateArrows();
  }

  /**
   * 遍历动画
   */
  async traverse(onVisit: (index: number) => void): Promise<void> {
    for (let i = 0; i < this.nodes.length; i++) {
      await this.highlight(i, 600);
      onVisit(i);
      await this.unhighlight(i);
    }
  }

  /**
   * 更新所有箭头
   */
  private updateArrows(): void {
    // 清除旧箭头
    for (const node of this.nodes) {
      if (node.arrow) {
        node.arrow.dispose();
        node.arrow = null;
      }
    }
    
    // 创建新箭头
    for (let i = 0; i < this.nodes.length - 1; i++) {
      const from = this.nodes[i].position;
      const to = this.nodes[i + 1].position;
      this.nodes[i].arrow = this.createArrow(from, to);
    }
  }

  /**
   * 设置节点颜色
   */
  private setNodeColor(index: number, color: Color3): void {
    const node = this.nodes[index];
    if (node && node.mesh.material instanceof StandardMaterial) {
      node.mesh.material.diffuseColor = color;
    }
  }

  /**
   * 位置动画
   */
  private animatePosition(mesh: Mesh, target: Vector3, duration: number): Promise<void> {
    return new Promise(resolve => {
      const frames = Math.round(duration / 16.67);
      
      const animX = new Animation('moveX', 'position.x', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      animX.setKeys([{ frame: 0, value: mesh.position.x }, { frame: frames, value: target.x }]);
      
      const animY = new Animation('moveY', 'position.y', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      animY.setKeys([{ frame: 0, value: mesh.position.y }, { frame: frames, value: target.y }]);
      
      mesh.animations = [animX, animY];
      this.scene.beginAnimation(mesh, 0, frames, false, 1, resolve);
    });
  }

  /**
   * 淡出动画
   */
  private animateFadeOut(mesh: Mesh, duration: number): Promise<void> {
    return new Promise(resolve => {
      const frames = Math.round(duration / 16.67);
      
      const animation = new Animation(
        'fadeOut',
        'scaling',
        60,
        Animation.ANIMATIONTYPE_VECTOR3,
        Animation.ANIMATIONLOOPMODE_CONSTANT
      );
      
      animation.setKeys([
        { frame: 0, value: mesh.scaling.clone() },
        { frame: frames, value: new Vector3(0.01, 0.01, 0.01) }
      ]);
      
      mesh.animations = [animation];
      this.scene.beginAnimation(mesh, 0, frames, false, 1, resolve);
    });
  }

  /**
   * 重置
   */
  async reset(): Promise<void> {
    // 保存原始数据重新创建
    const values = this.nodes.map(n => n.value);
    await this.createFromArray(values);
  }

  /**
   * 清除
   */
  clear(): void {
    for (const node of this.nodes) {
      node.label.dispose();
      node.mesh.dispose();
      if (node.arrow) node.arrow.dispose();
    }
    this.nodes = [];
    this.highlightLayer.removeAllMeshes();
  }

  /**
   * 延迟
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
